import request from '../../../../util/APIUtils';
import { GATEWAY_BASE_URL, APDS_GATEWAY_BASE_URL } from '../../../../constants';
import { mapLearningPlansToRows } from '../utils/learning-plans.utils';
import rulesConfig from '../config/rules.json';
import programsConfig from '../config/programs.json';

export const fetchLearningPlans = async (actions = {}, classes = {}) => {
  const response = await request({
    url: `${GATEWAY_BASE_URL}/api/learning-plans`,
    method: 'GET',
    throw400Error: true
  });

  return mapLearningPlansToRows(response, actions, classes);
};


export const fetchLearningPlanDetails = async (learningPlanId) => {
  const response = await request({
    url: `${GATEWAY_BASE_URL}/api/learning-plans/${learningPlanId}/details`,
    method: 'GET',
    throw400Error: true
  });

  if (!response) {
    return {
      id: null,
      title: '',
      key: '',
      description: '',
      pillars: []
    };
  }

  return {
    id: response.id,
    title: response.title,
    key: response.key,
    description: response.description,
    pillars: response.pillars || []
  };
};

/**
 * Fetches programs from JSON file
 * Currently loads from internal JSON file, but structured to easily switch to API call in future
 * Future: GET /api/learning-plans/programs
 * @returns {Promise<Array>} Promise that resolves to programs array
 */
const fetchPrograms = async () => {
  // For now, return programs from JSON config
  // Could be fetched from API in future
  return Promise.resolve(programsConfig);
};

/**
 * Mapping of fieldSet names to their fetch functions
 * This allows different field sets to be loaded from different sources (file, API, etc.)
 */
const FIELD_SET_FETCHERS = {
  program_fields: fetchPrograms,
  // Add more field set mappings here as needed
};

/**
 * Fetches a specific field set by name
 * Uses the mapping to determine which method to call
 * @param {string} fieldSetName - Name of the field set to fetch
 * @returns {Promise<Array>} Promise that resolves to field array
 */
const fetchFieldSet = async (fieldSetName) => {
  const fetcher = FIELD_SET_FETCHERS[fieldSetName];
  
  if (!fetcher) {
    console.warn(`No fetcher found for field set: ${fieldSetName}`);
    return Promise.resolve([]);
  }
  
  return fetcher();
};

/**
 * Fetches all required field sets based on fieldRefs in rule types
 * @param {Array} ruleTypes - Array of rule types that may contain fieldRefs
 * @returns {Promise<Object>} Promise that resolves to fieldSets object
 */
const fetchFieldSets = async (ruleTypes) => {
  // Extract unique fieldRef values from rule types
  const fieldSetNames = [...new Set(
    ruleTypes
      .map(rt => rt.fieldRef)
      .filter(Boolean)
  )];
  
  // Fetch all required field sets in parallel
  const fieldSetPromises = fieldSetNames.map(async (fieldSetName) => {
    const fields = await fetchFieldSet(fieldSetName);
    return { [fieldSetName]: fields };
  });
  
  const fieldSetResults = await Promise.all(fieldSetPromises);
  
  // Combine all field sets into a single object
  return fieldSetResults.reduce((acc, fieldSet) => {
    return { ...acc, ...fieldSet };
  }, {});
};

/**
 * Resolves field references in rule types
 * If a rule type has a fieldRef, it replaces it with the actual fields from fieldSets
 * @param {Object} config - Rules configuration object
 * @param {Object} fieldSets - Field sets object containing shared field definitions
 * @returns {Object} Configuration with resolved field references
 */
const resolveFieldReferences = (config, fieldSets) => {
  if (!config.ruleTypes) {
    return config;
  }

  const resolvedRuleTypes = config.ruleTypes.map(ruleType => {
    // If rule type has fieldRef, resolve it from fieldSets
    if (ruleType.fieldRef && fieldSets[ruleType.fieldRef]) {
      return {
        ...ruleType,
        fields: fieldSets[ruleType.fieldRef],
        // Remove fieldRef after resolving
        fieldRef: undefined
      };
    }
    return ruleType;
  });

  return {
    ...config,
    ruleTypes: resolvedRuleTypes
  };
};

/**
 * Fetches rule types configuration
 * Currently loads from internal JSON file, but structured to easily switch to API call in future
 * Future: GET /api/learning-plans/rule-types
 * @returns {Promise<Object>} Promise that resolves to rules configuration object with resolved field references
 */
export const fetchRuleTypes = async () => {  
  // For now, load from JSON config
  // In future, could be fetched from API
  const config = rulesConfig;
  
  // Fetch field sets based on fieldRefs in rule types
  // Each field set is fetched using its mapped fetcher function
  const fieldSets = await fetchFieldSets(config.ruleTypes || []);
  
  // Resolve field references using the fetched field sets
  const resolvedConfig = resolveFieldReferences(config, fieldSets);
  
  return Promise.resolve(resolvedConfig);
};

/**
 * Saves pillar rules configuration
 * @param {string} planId - Learning plan ID
 * @param {string} pillarId - Pillar ID
 * @param {Object} rulesConfig - Rules configuration object to save
 * @returns {Promise<Object>} Promise that resolves to success response
 */
export const savePillarRules = async (planId, pillarId, rulesConfig) => {
  return request({
    url: `${GATEWAY_BASE_URL}/api/learning-plans/${planId}/pillars/${pillarId}/rules`,
    method: 'PUT',
    body: JSON.stringify({ rulesJson: rulesConfig }),
    throw400Error: true
  });
};

/**
 * Fetches learning plan report for a specific learner
 * @param {string|number} learningPlanId - Learning plan ID (planKey)
 * @param {string|number} learnerId - Learner/User ID
 * @returns {Promise<Object>} Promise that resolves to report data
 */
export const fetchLearningPlanReport = async (learningPlanId, learnerId) => {
  const response = await request({
    url: `${APDS_GATEWAY_BASE_URL}api/v1/learning-plans/${learningPlanId}/reports/learners/${learnerId}`,
    method: 'GET',
    throw400Error: true
  });

  return response?.payload || response;
};

/**
 * Calculate progress percentages for a pillar
 * @param {number} completed - Number of completed learners
 * @param {number} inProgress - Number of in-progress learners
 * @returns {Object} Object containing total, completedPercent, and inProgressPercent
 */
const calculatePillarProgress = (completed, inProgress) => {
  const total = completed + inProgress;
  const completedPercent = total > 0 ? Math.round((completed / total) * 100 * 100) / 100 : 0;
  const inProgressPercent = total > 0 ? Math.round((inProgress / total) * 100 * 100) / 100 : 0;

  return {
    total,
    completedPercent,
    inProgressPercent,
  };
};

/**
 * Transform API response pillars to component format
 * @param {Array} apiPillars - Array of pillar data from API
 * @returns {Array} Transformed array of pillar objects with calculated progress
 */
const transformPillarData = (apiPillars) => {
  if (!apiPillars || !Array.isArray(apiPillars)) {
    return [];
  }

  return apiPillars.map((pillar) => {
    const completed = pillar.completedLearnersCount || 0;
    const inProgress = pillar.inProgressLearnersCount || 0;
    const progress = calculatePillarProgress(completed, inProgress);

    return {
      id: pillar.pillarId,
      title: pillar.pillarName,
      completed: completed,
      inProgress: inProgress,
      total: progress.total,
      completedPercent: progress.completedPercent,
      inProgressPercent: progress.inProgressPercent,
    };
  });
};

/**
 * Fetches aggregate progress report for a learning plan
 * @param {string|number} planId - Learning plan ID
 * @returns {Promise<Object>} Promise that resolves to aggregate report data with transformed pillars
 */
export const fetchLearningPlanAggregateReport = async (planId) => {
  const response = await request({
    url: `${GATEWAY_BASE_URL}/api/learning-plans/${planId}/reports/aggregate`,
    method: 'GET',
    throw400Error: true
  });

  if (!response) {
    return {
      learningPlanId: null,
      pillars: []
    };
  }

  // Transform API data to component format
  const transformedPillars = transformPillarData(response.pillars);

  return {
    learningPlanId: response.learningPlanId,
    pillars: transformedPillars
  };
};

